"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { BuilderProvider, useBuilder } from "@/context/BuilderContext";
import { useCart } from "@/context/CartContext";
import { useToast } from "@/context/ToastContext";
import { useRouter } from "next/navigation";
import { BUILDER_STEPS } from "@/lib/constants";
import { useSiteConfig } from "@/context/SiteConfigContext";
import ComingSoon from "@/components/ComingSoon";
import { formatPrice } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { BuilderStepper } from "@/components/builder/BuilderStepper";
import { BuilderPreview } from "@/components/builder/BuilderPreview";
import { StepHairType } from "@/components/builder/StepHairType";
import { StepBaseType } from "@/components/builder/StepBaseType";
import { StepLength } from "@/components/builder/StepLength";
import { StepTexture } from "@/components/builder/StepTexture";
import { StepColor } from "@/components/builder/StepColor";
import { StepCapSize } from "@/components/builder/StepCapSize";
import { StepExtras } from "@/components/builder/StepExtras";
import { ChevronLeft, ChevronRight, ShoppingBag, RotateCcw } from "lucide-react";
import { useTranslations } from "next-intl";
import { trackEvent } from "@/lib/track";

function toSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Map a builder step's snake_case key (e.g. `base_type`, `hair_type`)
 * to its camelCase i18n key (e.g. `baseType`, `hairType`). The
 * translation files use camelCase but the constants use snake_case to
 * match the BuilderContext + cart serialization layer. This helper
 * keeps the mapping in one place — adding a new step now means adding
 * one entry here AND the matching `<key>` + `<key>Desc` to en.json.
 *
 * Used to be a 2-arm conditional inline. Was missing `hair_type` →
 * `hairType` which broke the production build for 3 commits in a row.
 */
function stepKeyToI18n(key: string): string {
  switch (key) {
    case "hair_type":
      return "hairType";
    case "base_type":
      return "baseType";
    case "cap_size":
      return "capSize";
    default:
      return key;
  }
}

const STEP_COMPONENTS = [
  StepHairType,
  StepBaseType,
  StepLength,
  StepTexture,
  StepColor,
  StepCapSize,
  StepExtras,
];

function BuilderContent() {
  const {
    currentStep,
    selections,
    totalPrice,
    priceBreakdown,
    specialNotes,
    nextStep,
    prevStep,
    canProceed,
    isLastStep,
    isFirstStep,
    reset,
  } = useBuilder();
  const { addItem } = useCart();
  const router = useRouter();
  const t = useTranslations("builder");
  const tc = useTranslations("common");

  const { toast } = useToast();

  // Refs for the scrollable panes: options panel scrolls on desktop,
  // the whole preview+options area scrolls on mobile (preview scrolls
  // away so all options are reachable on small screens)
  const optionsPanelRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Scroll to top when the builder step changes (fixes mobile UX — issue #24)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    optionsPanelRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    scrollAreaRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    // Track builder steps
    const stepName = BUILDER_STEPS[currentStep]?.key || `step_${currentStep}`;
    trackEvent(currentStep === 0 ? "builder_start" : "builder_step", {
      step: currentStep,
      stepName,
      selections: { ...selections },
    });
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track transition direction for slide animations
  const [direction, setDirection] = useState<"forward" | "backward">("forward");

  // Add-to-cart flow states: idle → adding → added
  const [cartState, setCartState] = useState<"idle" | "adding" | "added">("idle");
  const addToCartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup pending add-to-cart timer on unmount
  useEffect(() => {
    return () => {
      if (addToCartTimerRef.current) {
        clearTimeout(addToCartTimerRef.current);
        addToCartTimerRef.current = null;
      }
    };
  }, []);

  const handleNext = useCallback(() => {
    setDirection("forward");
    nextStep();
  }, [nextStep]);

  const handlePrev = useCallback(() => {
    setDirection("backward");
    prevStep();
  }, [prevStep]);

  const StepComponent = STEP_COMPONENTS[currentStep];
  const stepInfo = BUILDER_STEPS[currentStep];

  const handleAddToCart = () => {
    if (cartState !== "idle") return;
    setCartState("adding");

    // Brief loading delay for perceived quality
    if (addToCartTimerRef.current) clearTimeout(addToCartTimerRef.current);
    addToCartTimerRef.current = setTimeout(() => {
      const configEntries: Record<string, string> = {};
      for (const [key, value] of Object.entries(selections)) {
        if (value) configEntries[key] = value;
      }
      if (specialNotes) configEntries.notes = specialNotes;

      // Determine the best preview image (same logic as BuilderPreview)
      const hasCombo = selections.texture && selections.color && selections.color !== "Custom Color";
      const comboPath = hasCombo
        ? `/builder/combo/${toSlug(selections.texture!)}--${toSlug(selections.color!)}.webp`
        : null;
      const fallbackOrder = ["color", "texture", "length", "base_type"] as const;
      const singlePath = (() => {
        for (const key of fallbackOrder) {
          const val = selections[key];
          if (val) return `/builder/${key}/${toSlug(val)}.webp`;
        }
        return null;
      })();
      const previewImage = comboPath || singlePath || undefined;

      addItem({
        name: "Custom Built Wig",
        price: totalPrice,
        quantity: 1,
        image: previewImage,
        customConfig: configEntries,
      });

      toast(t("addedToCart"), "success");
      setCartState("added");
    }, 1000);
  };

  const handleViewCart = () => {
    reset();
    router.push("/cart");
  };

  const handleContinueShopping = () => {
    reset();
    router.push("/");
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _addOnTotal = Object.values(priceBreakdown).reduce((sum, val) => sum + val, 0);

  return (
    <div className="h-[calc(100vh-3.5rem)] supports-[height:1dvh]:h-[calc(100dvh-3.5rem)] flex flex-col bg-gradient-to-b from-surface to-brand-cream/30">
      {/* ─── Top: Stepper (fixed) ────────────────────────── */}
      <div className="flex-shrink-0 px-4 sm:px-8 pt-3 pb-3 sm:pt-8 sm:pb-6 border-b border-theme-border/30 bg-surface/80 backdrop-blur-sm z-30 relative overflow-hidden">
        <BuilderStepper />
      </div>

      {/* ─── Main area: Preview + Options side by side on desktop, stacked on mobile ── */}
      <div
        ref={scrollAreaRef}
        className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-y-auto lg:overflow-visible"
      >
        {/* ─── Left/Top: Preview (scrolls away on mobile; sticky pane on desktop) ── */}
        <div className="flex-shrink-0 lg:flex-shrink lg:flex-1 lg:sticky lg:top-0 h-[42vh] sm:h-[46vh] lg:h-full min-h-[300px] max-h-[460px] lg:max-h-none bg-gradient-to-b from-background to-brand-cream relative overflow-hidden">
          <BuilderPreview />
        </div>

        {/* ─── Right/Bottom: Options (scrollable) ─────────── */}
        <div ref={optionsPanelRef} className="flex-1 lg:flex-1 lg:max-w-xl xl:max-w-2xl lg:overflow-y-auto lg:border-s lg:border-theme-border/30">
          <div className="px-4 sm:px-8 lg:px-10 py-8 sm:py-10 pb-24 sm:pb-10">
            {/* Step Header */}
            <div className="mb-8">
              <p className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-brand-gold font-medium mb-2.5">
                {t("stepOf", { current: currentStep + 1, total: BUILDER_STEPS.length })}
              </p>
              <h1 className="font-display text-2xl sm:text-[2rem] lg:text-4xl text-foreground tracking-tight">
                {t(stepKeyToI18n(stepInfo.key))}
              </h1>
              <p className="text-brand-muted mt-2 text-sm sm:text-base leading-relaxed max-w-lg">
                {t(`${stepKeyToI18n(stepInfo.key)}Desc`)}
              </p>
            </div>

            {/* Step Content — slides left/right based on direction */}
            <div className="overflow-hidden">
              <div
                key={currentStep}
                className={direction === "forward" ? "step-enter" : "step-enter-back"}
              >
                <StepComponent />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Bottom: Sticky Price + Navigation Bar ────────── */}
      <div className="flex-shrink-0 border-t border-theme-border/30 bg-surface/95 backdrop-blur-md px-4 sm:px-8 lg:px-16 py-3 sm:py-5 z-50 relative shadow-[0_-4px_20px_rgba(0,0,0,0.03)] pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          {/* Left: Back + Start Over */}
          <div className="flex items-center gap-1">
            <button
              onClick={handlePrev}
              disabled={isFirstStep}
              className={cn(
                "flex items-center justify-center gap-1.5 px-5 sm:px-7 py-3 min-h-[44px] min-w-[44px] text-sm font-medium tracking-wide uppercase rounded-sm transition-all duration-500",
                isFirstStep
                  ? "text-foreground-muted/30 cursor-not-allowed"
                  : "text-foreground hover:text-brand-gold active:scale-[0.98]"
              )}
            >
              <ChevronLeft className="w-4 h-4 transition-transform duration-500 group-hover:-translate-x-0.5" />
              <span className="hidden sm:inline">{tc("back")}</span>
            </button>
            {!isFirstStep && (
              <button
                onClick={() => { reset(); setCartState("idle"); }}
                className="flex items-center gap-1.5 px-3 py-1.5 min-h-[44px] text-[10px] sm:text-xs text-red-400 hover:text-red-500 tracking-wide uppercase transition-colors duration-300"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Restart
              </button>
            )}
          </div>

          {/* Center: Price — animated on change */}
          <div className="text-center flex-1">
            <div
              key={totalPrice}
              className="flex items-baseline justify-center gap-2.5 animate-count-up animate-price-pulse"
            >
              <span className="font-display text-xl sm:text-2xl lg:text-[1.65rem] text-foreground tracking-tight">
                {formatPrice(totalPrice)}
              </span>
              {totalPrice > 0 && (
                <span className="hidden sm:inline text-[10px] sm:text-xs text-brand-muted/70 font-medium">
                  Includes bleached knots, baby hairs &amp; pre-plucked hairline
                </span>
              )}
            </div>
            <p className="text-[10px] sm:text-xs text-brand-muted/60 mt-1 tracking-wide uppercase">
              {t("freeShipping")}
            </p>
          </div>

          {/* Right: Next / Add to Cart */}
          {isLastStep ? (
            cartState === "added" ? (
              /* ─── Post-add: View Cart / Continue Shopping ─── */
              <div className="flex items-center gap-2">
                <button
                  onClick={handleViewCart}
                  className="flex items-center justify-center gap-1.5 px-4 sm:px-6 py-3 min-h-[44px] bg-brand-gold text-white text-sm font-medium tracking-widest uppercase rounded-sm shadow-md shadow-brand-gold/20 transition-all duration-500 hover:bg-brand-gold-dark hover:shadow-lg hover:shadow-brand-gold/30 active:scale-[0.98] animate-fade-in"
                >
                  <ShoppingBag className="w-4 h-4" />
                  <span className="hidden sm:inline">{t("viewCart")}</span>
                </button>
                <button
                  onClick={handleContinueShopping}
                  className="flex items-center justify-center gap-1.5 px-4 sm:px-6 py-3 min-h-[44px] text-sm font-medium tracking-wide uppercase text-foreground hover:text-brand-gold transition-colors duration-300 animate-fade-in"
                >
                  <span className="hidden sm:inline">{t("continueShopping")}</span>
                  <span className="sm:hidden">{tc("back")}</span>
                </button>
              </div>
            ) : (
              /* ─── Add to Cart / Adding... / Added! ─── */
              <button
                onClick={handleAddToCart}
                disabled={cartState === "adding"}
                className={cn(
                  "flex items-center justify-center gap-2 px-6 sm:px-8 py-3 min-h-[44px] text-sm font-medium tracking-widest uppercase rounded-sm shadow-md transition-all duration-500 active:scale-[0.98]",
                  cartState === "adding"
                    ? "bg-brand-gold/70 text-white cursor-wait shadow-brand-gold/10"
                    : "bg-brand-gold text-white shadow-brand-gold/20 hover:bg-brand-gold-dark hover:shadow-lg hover:shadow-brand-gold/30"
                )}
              >
                {cartState === "adding" ? (
                  <>
                    <svg
                      className="w-4 h-4 animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="3"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    <span className="hidden sm:inline">{t("adding")}</span>
                  </>
                ) : (
                  <>
                    <ShoppingBag className="w-4 h-4" />
                    <span className="hidden sm:inline">{t("addToCart")}</span>
                    <span className="sm:hidden">{t("add")}</span>
                  </>
                )}
              </button>
            )
          ) : (
            <div className="text-center">
              <button
                onClick={handleNext}
                disabled={!canProceed}
                className={cn(
                  "flex items-center justify-center gap-1.5 px-6 sm:px-8 py-3 min-h-[44px] min-w-[44px] text-sm font-medium tracking-widest uppercase rounded-sm transition-all duration-500",
                  canProceed
                    ? "bg-brand-dark text-white hover:bg-brand-gold hover:shadow-lg hover:shadow-brand-gold/20 shadow-md active:scale-[0.98]"
                    : "bg-theme-border text-foreground-muted cursor-not-allowed"
                )}
              >
                <span className="hidden sm:inline">{t("continue")}</span>
                <span className="sm:hidden">{tc("next")}</span>
                <ChevronRight className="w-4 h-4" />
              </button>
              {!canProceed && (
                <p className="text-xs text-brand-muted mt-2 animate-fade-in">
                  {t("selectOption")}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BuilderPage() {
  const { comingSoon } = useSiteConfig();
  const tcs = useTranslations("comingSoon");
  if (comingSoon.builder) {
    return (
      <ComingSoon
        title={tcs("builderTitle")}
        body={tcs("builderBody")}
        secondaryCta={{ label: tcs("ctaBookConsultation"), href: "/book?service=new_wig" }}
        backgroundImage="/images/pages/builder-hero.jpg"
      />
    );
  }
  return (
    <BuilderProvider>
      <BuilderContent />
    </BuilderProvider>
  );
}
